/**
 * 購入開始処理のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import { push } from 'connected-react-router';
import { beginFetch, endFetch, failedFetch } from 'common/modules/app/fetch';
import {
  resetPurchaseSessionPlanId,
  setPurchaseSessionPlanId
} from 'common/modules/app/purchaseSession';
import { purchaseError, ErrorResponse } from 'common/modules/app/errors';
import { setStatus, setPlanId, BoardStatus } from 'common/modules/app/board';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import { postPurchaseSessionRequest } from 'common/utils/request/sas-request';
import { PostPurchaseSessionResponse } from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { SasPagePath } from 'common/utils/urls';
import {
  SasCookieKeys,
  loadCookie,
  deleteCookie
} from 'common/utils/cookie-utils';
import { postAccessToken } from 'common/utils/token';
import { loggedOut } from 'common/modules/app/principal';

enum ActionTypes {
  RESET_STATE = 'domain/purchaseSession/RESET_STATE',
  FETCH_SUCCESS = `domain/purchaseSession/FETCH_SUCCESS`,
  SET_PURCHASE_STATE = `domain/purchaseSession/SET_PURCHASE_STATE`,
  SET_PURCHASE_URL = 'domain/purchaseSession/SET_PURCHASE_URL'
}

/**
 * 購入開始APIの戻り値Stateの値
 */
export enum PurchaseStateType {
  // 購入
  TYPE_PURCHASE = 'purchase',
  // 購入済み
  TYPE_PURCHASED = 'purchased',
  // 一部重複
  TYPE_PARTIAL = 'partial',
  // 追加購入可能(利用制限のあるプランのみ)
  TYPE_ADDITIONAL = 'additional',
  // 重複購入可能(重複購入可能なプランのみ)
  TYPE_DUPLICATE_PURCHASE = 'duplicate_purchase',
  // 重複購入不可(重複購入可能なプランのみ)
  TYPE_DUPLICATE_PURCHASED = 'duplicate_purchased',
  // 決済画面表示
  // Cleverbridgeの決済画面表示を表すState
  // 購入開始APIが戻す値ではないが、管理の都合上こちらに追加
  TYPE_DISPLAY_PAYMENT = 'display_payment'
}

export const POST_PURCHASE_SESSION_ID = 'post_purchase_session';

/**
 * 購入開始処理のStateをクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * 購入開始処理のStateを置き換える
 */
export const fetchSuccess = (payload: PostPurchaseSessionResponse) => {
  return createAction(ActionTypes.FETCH_SUCCESS, payload);
};

/**
 * 購入プランの状態を更新する
 * 一部購入済みで、購入を続行する場合に利用
 */
export const setPurchaseState = (payload: PurchaseStateType) => {
  return createAction(ActionTypes.SET_PURCHASE_STATE, payload);
};

/**
 * 購入プランのURLを更新する
 */
export const setPurchaseUrl = (payload: string) => {
  return createAction(ActionTypes.SET_PURCHASE_URL, payload);
};

const actions = {
  resetState,
  fetchSuccess,
  setPurchaseState,
  setPurchaseUrl
};
type PurchaseSessionAction = ActionsUnion<typeof actions>;

/**
 * 支払処理を開始する
 * @param token: 認証トークン
 * @param refreshToken: リフレッシュトークン
 * @param planId: プランID
 * @param planName: プラン名
 * @param country: 国
 * @param language: 言語コード
 * @param vendorId: 支払いベンダーID
 * @param isRetry: 再実施か否か
 */
export const postPurchaseSession = (
  token: string,
  refreshToken: string,
  planId: string,
  planName: string,
  country: string,
  language: string,
  vendorId: string,
  isRetry?: boolean
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>
  ) => {
    // POST_PURCHASE_SESSION_IDを処理中に変更
    dispatch(beginFetch(POST_PURCHASE_SESSION_ID));
    // 購入開始処理中のプランIDを設定
    dispatch(setPurchaseSessionPlanId(planId));

    // API呼び出し前に情報をクリアする
    await dispatch(resetState());

    // 購入開始APIを実行
    const requestData = postPurchaseSessionRequest(
      token,
      vendorId,
      planId,
      country,
      language
    );
    const response = await request(
      requestData.url,
      requestData.config,
      false,
      true
    );
    // API実行結果がエラーかどうか判断
    if (response.error) {
      // エラー処理
      const error = response.error as ErrorResponse;
      error.requestId = POST_PURCHASE_SESSION_ID;
      // 403エラーの場合はトークン更新後に再実施を行う
      // 但し、再実施で起きた場合は対象外とする
      let needLogout = false;
      if (error.statusCode && error.statusCode === 403) {
        if (isRetry === undefined || !isRetry) {
          // トークン更新
          await dispatch(postAccessToken(token, refreshToken));
          // トークン更新に成功した場合は再実施
          const cookie = loadCookie();
          if (cookie[SasCookieKeys.KEY_TOKEN] !== token) {
            await dispatch(
              postPurchaseSession(
                cookie[SasCookieKeys.KEY_TOKEN],
                cookie[SasCookieKeys.KEY_REFRESH_TOKEN],
                planId,
                planName,
                country,
                language,
                vendorId,
                true
              )
            );
            // 購入開始処理中のプランIDをリセット
            dispatch(resetPurchaseSessionPlanId());
            // 最終的な結果は2度目の呼び出しになるため、1度目の呼び出しはここで完了にする
            return;
          } else {
            // トークン更新に失敗の場合はログアウト状態にさせる
            needLogout = true;
          }
        } else {
          // 再実施でも403だった時はログアウト状態にさせる
          needLogout = true;
        }
      }
      if ((error.statusCode && error.statusCode === 401) || needLogout) {
        // 最終的に401または403であった場合はSASのみログアウト状態にする
        deleteCookie(SasCookieKeys.KEY_TOKEN);
        deleteCookie(SasCookieKeys.KEY_REFRESH_TOKEN);
        dispatch(loggedOut());
      }
      // 購入開始処理中のプランIDをリセット
      dispatch(resetPurchaseSessionPlanId());
      // 403エラーでトークン更新に成功した以外は購入手続きエラー画面へ遷移
      dispatch(purchaseError(error));
      dispatch(push(SasPagePath.Error));
      dispatch(failedFetch(POST_PURCHASE_SESSION_ID));
      return;
    } else {
      // 成功の場合、戻り値の購入状態(state)によりエラー表示、または後続の外部支払い画面を表示する
      const body: PostPurchaseSessionResponse = (response.body as unknown) as PostPurchaseSessionResponse;
      // 結果を格納
      dispatch(fetchSuccess(body));
    }

    // 購入確認画面を表示
    dispatch(setPlanId(planId));
    dispatch(setStatus(BoardStatus.BEFORE_PURCHASE_DISP));

    // 購入開始処理中のプランIDをリセット
    dispatch(resetPurchaseSessionPlanId());

    // POST_PURCHASE_SESSION_IDを未処理に変更
    dispatch(endFetch(POST_PURCHASE_SESSION_ID));
  };
};

/**
 * 購入開始処理のStateをクリアする
 */
export const reset = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * 購入開始処理のState
 * 購入開始APIの戻り値のbodyをそのまま持たせる
 */
export interface PurchaseSessionState {
  state: string;
  purchaseUrl: string;
  usage: number | null;
  limit: number | null;
}

const initialState: PurchaseSessionState = {
  state: '',
  purchaseUrl: '',
  usage: null,
  limit: null
};

export default (
  state: PurchaseSessionState = initialState,
  actions: PurchaseSessionAction
): PurchaseSessionState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      return actions.payload;
    }
    case ActionTypes.RESET_STATE: {
      return initialState;
    }
    case ActionTypes.SET_PURCHASE_STATE: {
      return { ...state, state: actions.payload };
    }
    case ActionTypes.SET_PURCHASE_URL: {
      return { ...state, purchaseUrl: actions.payload };
    }
    default: {
      return state;
    }
  }
};
